import React, { useRef, useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { BADGES, formatINR } from '../../../data/games';

const GameCard = ({ game, onWhatsAppClick, onPreviewClick, layout = 'grid' }) => {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const ref = useRef(null);
  const badge = BADGES[game?.badge] ?? BADGES.classic;

  const handleMove = (e) => {
    if (layout !== 'grid') return;
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: -py * 10, y: px * 12 });
  };

  if (layout === 'list') {
    return (
      <div className="card-flat flex flex-col gap-5 p-5 transition hover:border-royal-gold/40 sm:flex-row sm:items-center">
        <div className="relative h-32 w-full shrink-0 overflow-hidden rounded-2xl sm:w-52">
          <Image src={game?.image} alt={game?.name} className="h-full w-full object-cover" />
          <span className={`absolute left-3 top-3 rounded-full bg-gradient-to-r ${badge.className} px-2 py-0.5 text-[0.58rem] font-bold text-[#150F02]`}>
            {badge.label}
          </span>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="font-heading text-xl font-bold text-white">{game?.name}</h3>
            <span className="rounded-full bg-white/[0.07] px-2.5 py-0.5 text-[0.68rem] text-white/55">{game?.category}</span>
            <span className="inline-flex items-center gap-1 text-xs text-royal-gold">
              <Icon name="Star" size={13} fill="#F0C24B" color="#F0C24B" /> {game?.rating}
            </span>
          </div>
          <p className="mt-2 line-clamp-2 text-sm text-white/55">{game?.description}</p>
          <div className="mt-3 flex flex-wrap gap-4 text-xs text-white/50">
            <span>Min <strong className="text-white/85">{formatINR(game?.minBet)}</strong></span>
            <span>Max win <strong className="gold-text">{formatINR(game?.maxWin)}</strong></span>
            <span className="text-wa-green">{game?.playerCount?.toLocaleString('en-IN')} playing</span>
          </div>
        </div>

        <div className="flex shrink-0 gap-2">
          <button onClick={() => onPreviewClick(game)} className="btn-3d btn-3d-ghost !px-4 !py-2.5 text-xs">
            Details
          </button>
          <button onClick={() => onWhatsAppClick(game)} className="btn-3d btn-3d-wa !px-4 !py-2.5 text-xs">
            <FaWhatsapp size={15} /> Play
          </button>
        </div>
      </div>
    );
  }

  return (
    <article
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={() => setTilt({ x: 0, y: 0 })}
      className="group relative flex h-full flex-col overflow-hidden rounded-royal border border-white/10 bg-white/[0.045] shadow-depth transition-all duration-500 ease-royal hover:border-royal-gold/45 hover:shadow-royal"
      style={{ transform: `perspective(1100px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) translateY(${tilt.x || tilt.y ? -6 : 0}px)` }}
    >
      {/* Art */}
      <div className="relative h-44 overflow-hidden">
        <Image
          src={game?.image}
          alt={`${game?.name} on Nitin Online Book`}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A12] via-transparent to-transparent" />

        {game?.isLive && (
          <span className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full border border-white/15 bg-black/60 px-2.5 py-1 text-[0.58rem] font-bold tracking-wider text-white backdrop-blur">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-victory-red" /> LIVE
          </span>
        )}

        <span className={`absolute right-3 top-3 flex items-center gap-1 rounded-full bg-gradient-to-r ${badge.className} px-2.5 py-1 text-[0.58rem] font-bold text-[#150F02]`}>
          <Icon name={badge.icon} size={10} color="#150F02" /> {badge.label}
        </span>

        {/* Hover action */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/55 opacity-0 backdrop-blur-[2px] transition-opacity duration-300 group-hover:opacity-100">
          <button
            onClick={() => onPreviewClick(game)}
            className="btn-3d !px-5 !py-2.5 text-xs"
          >
            <Icon name="Eye" size={15} /> Preview table
          </button>
        </div>

        <span className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-full bg-black/60 px-2.5 py-1 text-[0.62rem] text-white/80 backdrop-blur">
          <Icon name="Users" size={11} color="#25D366" />
          {game?.playerCount?.toLocaleString('en-IN')} playing
        </span>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate font-heading text-lg font-bold text-white">{game?.name}</h3>
            <p className="text-[0.7rem] text-white/45">{game?.category} · RTP {game?.rtp}</p>
          </div>
          <span className="flex shrink-0 items-center gap-1 rounded-full bg-royal-gold/12 px-2 py-1 text-xs font-semibold text-royal-gold">
            <Icon name="Star" size={12} fill="#F0C24B" color="#F0C24B" /> {game?.rating}
          </span>
        </div>

        <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-white/55">{game?.description}</p>

        <div className="my-4 grid grid-cols-2 gap-2.5">
          <div className="rounded-xl border border-white/8 bg-white/[0.04] px-3 py-2">
            <p className="text-[0.58rem] uppercase tracking-[0.14em] text-white/40">Min bet</p>
            <p className="font-display text-sm font-semibold text-white">{formatINR(game?.minBet)}</p>
          </div>
          <div className="rounded-xl border border-white/8 bg-white/[0.04] px-3 py-2">
            <p className="text-[0.58rem] uppercase tracking-[0.14em] text-white/40">Max win</p>
            <p className="font-display text-sm font-semibold gold-text">{formatINR(game?.maxWin)}</p>
          </div>
        </div>

        {game?.recentWinner && (
          <p className="mb-4 flex items-center gap-2 rounded-xl border border-wa-green/20 bg-wa-green/8 px-3 py-2 text-[0.7rem] text-white/70">
            <Icon name="Trophy" size={13} color="#25D366" />
            {game.recentWinner.name} just won
            <strong className="text-wa-green">{formatINR(game.recentWinner.amount)}</strong>
          </p>
        )}

        <div className="mt-auto flex gap-2">
          <button onClick={() => onWhatsAppClick(game)} className="btn-3d btn-3d-wa flex-1 !py-2.5 text-xs">
            <FaWhatsapp size={15} /> Play via WhatsApp
          </button>
          <button
            onClick={() => onPreviewClick(game)}
            aria-label="Game details"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/12 bg-white/[0.06] text-white/70 transition hover:border-royal-gold/60 hover:text-royal-gold"
          >
            <Icon name="Info" size={17} />
          </button>
        </div>

        {game?.features?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5 pt-0">
            {game.features.slice(0, 4).map((f) => (
              <span key={f} className="rounded-full bg-white/[0.06] px-2.5 py-1 text-[0.62rem] text-white/50">
                {f}
              </span>
            ))}
          </div>
        )}
      </div>
    </article>
  );
};

export default GameCard;
